import { Box, Heading, Page, Text } from '@zillow/constellation-10';
// oxlint-disable-next-line constellation/no-styled-factory -- pre-existing styled-components-style usage, not migrating in a lint opt-in
import { styled } from '@/styled-system/jsx';

import type { PropsWithChildren } from 'react';
import React from 'react';
import type { SystemStyleObject } from '@/styled-system/types';

type BgColorType = SystemStyleObject['backgroundColor'];
type BorderColorType = SystemStyleObject['borderColor'];
type BorderRadiusType = SystemStyleObject['borderRadius'];

// oxlint-disable-next-line constellation/no-styled-factory -- pre-existing styled-components-style usage, not migrating in a lint opt-in
const StyledContentContainer = styled('div', {
  base: {
    marginBottom: '600',
  },
});

export interface PageWrapperConfig {
  backgroundColor: BgColorType;
  fluid: boolean;
}
/* colors must be constellation names */
const defaultConfig: PageWrapperConfig = {
  backgroundColor: 'backgroundLight',
  fluid: false,
};

export interface PageWrapperConfigContent {
  backgroundColor: BgColorType;
  borderRadius: BorderRadiusType;
  borderColor: BorderColorType;
  padding?: string;
}

/* colors must be constellation names */
const defaultConfigContent: PageWrapperConfigContent = {
  backgroundColor: 'white',
  borderRadius: '5px',
  borderColor: 'gray.300',
};

export interface PageWrapperProps {
  title: string;
  subTitle?: string | JSX.Element;
  alert?: JSX.Element;
  widget?: JSX.Element;
  backButton?: JSX.Element;
  additionalBreadcrumbElement?: JSX.Element;
  configContent?: Partial<PageWrapperConfigContent>;
  config?: Partial<PageWrapperConfig>;
  multipleBlocks?: boolean;
  additionalOuterContent?: JSX.Element;
  className?: string;
}

export const PageWrapper = ({
  title,
  subTitle,
  backButton,
  widget,
  config: pConfig,
  configContent: pContentConfig,
  children,
  alert,
  multipleBlocks,
  additionalBreadcrumbElement,
  additionalOuterContent,
  className,
}: PropsWithChildren<PageWrapperProps>) => {
  const config: PageWrapperConfig = {
    ...defaultConfig,
    ...pConfig,
  };

  const configContent: PageWrapperConfigContent = {
    ...defaultConfigContent,
    ...pContentConfig,
  };

  const { backgroundColor, fluid } = config;

  const pageClasses = ['PageWrapper'];

  if (className) {
    pageClasses.push(className);
  }

  return (
    <Page.Root className={pageClasses.join(' ')} css={{ backgroundColor }} fluid={fluid}>
      {(backButton || additionalBreadcrumbElement) && (
        <Page.Breadcrumb
          css={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingInline: 'md' }}
        >
          {backButton}
          {additionalBreadcrumbElement}
        </Page.Breadcrumb>
      )}
      {title && (
        <Page.Header css={{ marginBottom: subTitle ? '200' : undefined, '& h1': { fontSize: 'heading-md' } }}>
          <Heading level={1}>{title}</Heading>
          {widget}
        </Page.Header>
      )}
      {subTitle && (
        <Page.Content css={{ marginBlock: '0' }}>
          <Text textStyle="body-sm">{subTitle}</Text>
        </Page.Content>
      )}
      {alert && <Box css={{ paddingInline: 'md' }}>{alert}</Box>}
      {additionalOuterContent && <Page.Content css={{ marginBlock: '0' }}>{additionalOuterContent}</Page.Content>}
      <Page.Content>
        {multipleBlocks ? (
          React.Children.toArray(children)
            .filter(Boolean)
            .map((child, i) => (
              <StyledContentContainer
                className="StyledContentContainer"
                key={i}
                css={{
                  backgroundColor: configContent.backgroundColor,
                  border: configContent.borderColor ? '1px solid' : undefined,
                  borderColor: configContent.borderColor === 'none' ? 'transparent' : configContent.borderColor,
                  borderRadius: configContent.borderRadius,
                  padding: configContent.padding,
                }}
              >
                {child}
              </StyledContentContainer>
            ))
        ) : (
          <StyledContentContainer
            className="StyledContentContainer"
            css={{
              backgroundColor: configContent.backgroundColor,
              border: configContent.borderColor ? '1px solid' : undefined,
              borderColor: configContent.borderColor === 'none' ? 'transparent' : configContent.borderColor,
              borderRadius: configContent.borderRadius,
              padding: configContent.padding,
            }}
          >
            {children}
          </StyledContentContainer>
        )}
      </Page.Content>
    </Page.Root>
  );
};

export default PageWrapper;
